import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";

interface ContactFormModalProps {
  open: boolean;
  onClose: () => void;
  templateSubject?: string;
  templateMessage?: string;
  showDatacenterOptions?: boolean;
  isDomainManagement?: boolean;
  isCMSHosting?: boolean;
}

const ContactFormModal: React.FC<ContactFormModalProps> = ({
  open,
  onClose,
  templateSubject = "",
  templateMessage = "",
  showDatacenterOptions = false,
  isDomainManagement = false,
  isCMSHosting = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: templateSubject,
    message: templateMessage,
    // Datacenter-specific fields
    operatingSystem: "",
    controlPanel: "",
    backupFrequency: "",
    bandwidth: "",
    ipAddresses: "",
    managedServices: "",
    // Domain management fields
    domainExtension: "",
    // CMS hosting fields
    cmsType: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Update form when template changes
  useEffect(() => {
    if (open) {
      setFormData((prev) => ({
        ...prev,
        subject: templateSubject,
        message: templateMessage,
      }));
    }
  }, [open, templateSubject, templateMessage]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const name = e.target.name;
    const value = e.target.value;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formType = isDomainManagement
        ? "domain_management"
        : isCMSHosting
        ? "cms_hosting"
        : showDatacenterOptions
        ? "datacenter_hosting"
        : "service_inquiry";

      const payload: Record<string, any> = {
        formType,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        subject: formData.subject,
        message: formData.message,
      };

      if (isDomainManagement) {
        payload.domainExtension = formData.domainExtension;
        payload.managedServices = formData.managedServices;
      } else if (showDatacenterOptions) {
        payload.operatingSystem = formData.operatingSystem;
        payload.controlPanel = formData.controlPanel;
        payload.backupFrequency = formData.backupFrequency;
        payload.bandwidth = formData.bandwidth;
        payload.ipAddresses = formData.ipAddresses;
        payload.managedServices = formData.managedServices;
      }

      if (isCMSHosting) {
        payload.cmsType = formData.cmsType;
      }

      const response = await fetch("https://contact.isshosting.com/form-submission", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: "Message sent successfully! We'll get back to you soon.",
          severity: "success",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: "",
          operatingSystem: "",
          controlPanel: "",
          backupFrequency: "",
          bandwidth: "",
          ipAddresses: "",
          managedServices: "",
          domainExtension: "",
          cmsType: "",
        });
        // Close modal after successful submission
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error("Failed to send message");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to send message. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            borderRadius: 3,
            border: "1px solid rgba(0, 168, 255, 0.3)",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            pb: 2,
            borderBottom: "1px solid rgba(0, 168, 255, 0.2)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 56,
              height: 56,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
              flexShrink: 0,
            }}
          >
            <EmailIcon sx={{ fontSize: 32, color: "white" }} />
          </Box>
          <Typography
            component="span"
            variant="h5"
            sx={{ fontWeight: 600, flexGrow: 1 }}
          >
            Contact Us
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
                bgcolor: "rgba(0, 168, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mb: 3, lineHeight: 1.8 }}
          >
            Fill out the form below and our team will get back to you within 24
            hours to discuss your requirements.
          </Typography>

          <form onSubmit={handleSubmit} id="contact-form">
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
                >
                  Contact Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Your Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Your Email *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company/Organization"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </Grid>

              {/* Domain Management Options */}
              {isDomainManagement && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        Domain Preferences
                      </Typography>
                      <Chip
                        label="Optional"
                        size="small"
                        sx={{
                          bgcolor: "rgba(0, 168, 255, 0.1)",
                          color: "primary.main",
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Domain Extension</InputLabel>
                      <Select
                        name="domainExtension"
                        value={formData.domainExtension}
                        onChange={handleChange}
                        label="Domain Extension"
                      >
                        <MenuItem value="">
                          <em>Select Extension</em>
                        </MenuItem>
                        <MenuItem value=".com">.com</MenuItem>
                        <MenuItem value=".net">.net</MenuItem>
                        <MenuItem value=".org">.org</MenuItem>
                        <MenuItem value=".info">.info</MenuItem>
                        <MenuItem value=".biz">.biz</MenuItem>
                        <MenuItem value=".co">.co</MenuItem>
                        <MenuItem value=".io">.io</MenuItem>
                        <MenuItem value=".me">.me</MenuItem>
                        <MenuItem value=".us">.us</MenuItem>
                        <MenuItem value=".xyz">.xyz</MenuItem>
                        <MenuItem value=".tech">.tech</MenuItem>
                        <MenuItem value=".dev">.dev</MenuItem>
                        <MenuItem value=".app">.app</MenuItem>
                        <MenuItem value=".cloud">.cloud</MenuItem>
                        <MenuItem value=".online">.online</MenuItem>
                        <MenuItem value=".store">.store</MenuItem>
                        <MenuItem value=".shop">.shop</MenuItem>
                        <MenuItem value=".site">.site</MenuItem>
                        <MenuItem value=".website">.website</MenuItem>
                        <MenuItem value=".space">.space</MenuItem>
                        <MenuItem value=".digital">.digital</MenuItem>
                        <MenuItem value=".media">.media</MenuItem>
                        <MenuItem value=".pro">.pro</MenuItem>
                        <MenuItem value=".expert">.expert</MenuItem>
                        <MenuItem value=".agency">.agency</MenuItem>
                        <MenuItem value=".consulting">.consulting</MenuItem>
                        <MenuItem value=".company">.company</MenuItem>
                        <MenuItem value=".business">.business</MenuItem>
                        <MenuItem value=".enterprises">.enterprises</MenuItem>
                        <MenuItem value=".solutions">.solutions</MenuItem>
                        <MenuItem value=".services">.services</MenuItem>
                        <MenuItem value=".network">.network</MenuItem>
                        <MenuItem value=".systems">.systems</MenuItem>
                        <MenuItem value=".technology">.technology</MenuItem>
                        <MenuItem value=".email">.email</MenuItem>
                        <MenuItem value=".host">.host</MenuItem>
                        <MenuItem value=".hosting">.hosting</MenuItem>
                        <MenuItem value=".server">.server</MenuItem>
                        <MenuItem value=".cloud">.cloud</MenuItem>
                        <MenuItem value=".domain">.domain</MenuItem>
                        <MenuItem value=".domains">.domains</MenuItem>
                        <MenuItem value=".website">.website</MenuItem>
                        <MenuItem value=".web">.web</MenuItem>
                        <MenuItem value=".link">.link</MenuItem>
                        <MenuItem value=".click">.click</MenuItem>
                        <MenuItem value=".download">.download</MenuItem>
                        <MenuItem value=".press">.press</MenuItem>
                        <MenuItem value=".news">.news</MenuItem>
                        <MenuItem value=".blog">.blog</MenuItem>
                        <MenuItem value=".tv">.tv</MenuItem>
                        <MenuItem value=".cc">.cc</MenuItem>
                        <MenuItem value=".ws">.ws</MenuItem>
                        <MenuItem value=".name">.name</MenuItem>
                        <MenuItem value=".mobi">.mobi</MenuItem>
                        <MenuItem value=".tel">.tel</MenuItem>
                        <MenuItem value=".asia">.asia</MenuItem>
                        <MenuItem value=".eu">.eu</MenuItem>
                        <MenuItem value=".uk">.uk</MenuItem>
                        <MenuItem value=".ca">.ca</MenuItem>
                        <MenuItem value=".au">.au</MenuItem>
                        <MenuItem value=".de">.de</MenuItem>
                        <MenuItem value=".fr">.fr</MenuItem>
                        <MenuItem value=".jp">.jp</MenuItem>
                        <MenuItem value=".cn">.cn</MenuItem>
                        <MenuItem value=".in">.in</MenuItem>
                        <MenuItem value=".br">.br</MenuItem>
                        <MenuItem value=".mx">.mx</MenuItem>
                        <MenuItem value=".nl">.nl</MenuItem>
                        <MenuItem value=".se">.se</MenuItem>
                        <MenuItem value=".no">.no</MenuItem>
                        <MenuItem value=".dk">.dk</MenuItem>
                        <MenuItem value=".fi">.fi</MenuItem>
                        <MenuItem value=".pl">.pl</MenuItem>
                        <MenuItem value=".it">.it</MenuItem>
                        <MenuItem value=".es">.es</MenuItem>
                        <MenuItem value=".ch">.ch</MenuItem>
                        <MenuItem value=".at">.at</MenuItem>
                        <MenuItem value=".be">.be</MenuItem>
                        <MenuItem value=".nz">.nz</MenuItem>
                        <MenuItem value=".sg">.sg</MenuItem>
                        <MenuItem value=".hk">.hk</MenuItem>
                        <MenuItem value=".tw">.tw</MenuItem>
                        <MenuItem value=".kr">.kr</MenuItem>
                        <MenuItem value=".ru">.ru</MenuItem>
                        <MenuItem value=".za">.za</MenuItem>
                        <MenuItem value=".ae">.ae</MenuItem>
                        <MenuItem value=".sa">.sa</MenuItem>
                        <MenuItem value=".tr">.tr</MenuItem>
                        <MenuItem value=".id">.id</MenuItem>
                        <MenuItem value=".ph">.ph</MenuItem>
                        <MenuItem value=".th">.th</MenuItem>
                        <MenuItem value=".vn">.vn</MenuItem>
                        <MenuItem value=".my">.my</MenuItem>
                        <MenuItem value=".sg">.sg</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Managed Services</InputLabel>
                      <Select
                        name="managedServices"
                        value={formData.managedServices}
                        onChange={handleChange}
                        label="Managed Services"
                      >
                        <MenuItem value="">
                          <em>Select Management Level</em>
                        </MenuItem>
                        <MenuItem value="Fully Managed">Fully Managed</MenuItem>
                        <MenuItem value="Partially Managed">Partially Managed</MenuItem>
                        <MenuItem value="Self-Managed">Self-Managed</MenuItem>
                        <MenuItem value="Custom Management Plan">Custom Management Plan</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* CMS Hosting Options */}
              {isCMSHosting && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        CMS Platform
                      </Typography>
                      <Chip
                        label="Optional"
                        size="small"
                        sx={{
                          bgcolor: "rgba(0, 168, 255, 0.1)",
                          color: "primary.main",
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>CMS Platform</InputLabel>
                      <Select
                        name="cmsType"
                        value={formData.cmsType}
                        onChange={handleChange}
                        label="CMS Platform"
                      >
                        <MenuItem value="">
                          <em>Select CMS Platform</em>
                        </MenuItem>
                        <MenuItem value="WordPress">WordPress</MenuItem>
                        <MenuItem value="Joomla">Joomla</MenuItem>
                        <MenuItem value="Drupal">Drupal</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Datacenter Options */}
              {showDatacenterOptions && !isDomainManagement && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        Hosting Preferences
                      </Typography>
                      <Chip
                        label="Optional"
                        size="small"
                        sx={{
                          bgcolor: "rgba(0, 168, 255, 0.1)",
                          color: "primary.main",
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Operating System</InputLabel>
                      <Select
                        name="operatingSystem"
                        value={formData.operatingSystem}
                        onChange={handleChange}
                        label="Operating System"
                      >
                        <MenuItem value="">
                          <em>Select OS</em>
                        </MenuItem>
                        <MenuItem value="Ubuntu 22.04 LTS">Ubuntu 22.04 LTS</MenuItem>
                        <MenuItem value="Ubuntu 20.04 LTS">Ubuntu 20.04 LTS</MenuItem>
                        <MenuItem value="CentOS 9">CentOS 9</MenuItem>
                        <MenuItem value="CentOS 7">CentOS 7</MenuItem>
                        <MenuItem value="Debian 12">Debian 12</MenuItem>
                        <MenuItem value="Debian 11">Debian 11</MenuItem>
                        <MenuItem value="Rocky Linux 9">Rocky Linux 9</MenuItem>
                        <MenuItem value="AlmaLinux 9">AlmaLinux 9</MenuItem>
                        <MenuItem value="Windows Server 2022">Windows Server 2022</MenuItem>
                        <MenuItem value="Windows Server 2019">Windows Server 2019</MenuItem>
                        <MenuItem value="Other/Custom">Other/Custom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Control Panel</InputLabel>
                      <Select
                        name="controlPanel"
                        value={formData.controlPanel}
                        onChange={handleChange}
                        label="Control Panel"
                      >
                        <MenuItem value="">
                          <em>Select Control Panel</em>
                        </MenuItem>
                        <MenuItem value="WHM/cPanel">WHM/cPanel</MenuItem>
                        <MenuItem value="Plesk">Plesk</MenuItem>
                        <MenuItem value="DirectAdmin">DirectAdmin</MenuItem>
                        <MenuItem value="Webmin">Webmin</MenuItem>
                        <MenuItem value="None - Command Line Only">None - Command Line Only</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Backup Frequency</InputLabel>
                      <Select
                        name="backupFrequency"
                        value={formData.backupFrequency}
                        onChange={handleChange}
                        label="Backup Frequency"
                      >
                        <MenuItem value="">
                          <em>Select Frequency</em>
                        </MenuItem>
                        <MenuItem value="Daily">Daily</MenuItem>
                        <MenuItem value="Weekly">Weekly</MenuItem>
                        <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                        <MenuItem value="Monthly">Monthly</MenuItem>
                        <MenuItem value="Real-time/Continuous">Real-time/Continuous</MenuItem>
                        <MenuItem value="Custom Schedule">Custom Schedule</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Bandwidth Requirements</InputLabel>
                      <Select
                        name="bandwidth"
                        value={formData.bandwidth}
                        onChange={handleChange}
                        label="Bandwidth Requirements"
                      >
                        <MenuItem value="">
                          <em>Select Bandwidth</em>
                        </MenuItem>
                        <MenuItem value="1 TB/month">1 TB/month</MenuItem>
                        <MenuItem value="5 TB/month">5 TB/month</MenuItem>
                        <MenuItem value="10 TB/month">10 TB/month</MenuItem>
                        <MenuItem value="Unmetered (1 Gbps)">Unmetered (1 Gbps)</MenuItem>
                        <MenuItem value="Unmetered (10 Gbps)">Unmetered (10 Gbps)</MenuItem>
                        <MenuItem value="Custom">Custom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>IP Addresses Needed</InputLabel>
                      <Select
                        name="ipAddresses"
                        value={formData.ipAddresses}
                        onChange={handleChange}
                        label="IP Addresses Needed"
                      >
                        <MenuItem value="">
                          <em>Select IP Count</em>
                        </MenuItem>
                        <MenuItem value="1 IPv4">1 IPv4</MenuItem>
                        <MenuItem value="2-5 IPv4">2-5 IPv4</MenuItem>
                        <MenuItem value="6-10 IPv4">6-10 IPv4</MenuItem>
                        <MenuItem value="10+ IPv4">10+ IPv4</MenuItem>
                        <MenuItem value="IPv6 Block">IPv6 Block</MenuItem>
                        <MenuItem value="Both IPv4 and IPv6">Both IPv4 and IPv6</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Managed Services</InputLabel>
                      <Select
                        name="managedServices"
                        value={formData.managedServices}
                        onChange={handleChange}
                        label="Managed Services"
                      >
                        <MenuItem value="">
                          <em>Select Management Level</em>
                        </MenuItem>
                        <MenuItem value="Fully Managed">Fully Managed</MenuItem>
                        <MenuItem value="Partially Managed">Partially Managed</MenuItem>
                        <MenuItem value="Self-Managed">Self-Managed</MenuItem>
                        <MenuItem value="Custom Management Plan">Custom Management Plan</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Message Section */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1, mt: 2, color: "primary.main" }}
                >
                  Your Inquiry
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject *"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  variant="outlined"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message *"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  rows={6}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                borderColor: "primary.light",
                bgcolor: "rgba(0, 168, 255, 0.1)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="contact-form"
            variant="contained"
            disabled={loading}
            sx={{
              background: "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5dcdff 0%, #3385db 100%)",
                boxShadow: "0 8px 24px rgba(0, 168, 255, 0.4)",
              },
              "&:disabled": {
                background: "rgba(0, 168, 255, 0.5)",
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} sx={{ color: "white" }} />
                Sending...
              </Box>
            ) : (
              "Send Message"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactFormModal;
